"use client";
import { useModal } from "@/stores/use-modal-store";
import { Text } from "@/components/ui/text";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ManualUpload } from "@/components/invite/manual-upload";
import { BulkUpload } from "@/components/invite/bulk-upload";
import { ShareLink } from "@/components/invite/share-link";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { use, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { useMemberQuery } from "@/hooks/use-member-query";
import { ArrowLeft2, Document, Link1, Sms } from "iconsax-react";
import Quill from "quill";
import Box from "@/components/ui/box";
import { FormikProps } from "formik";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { filter, set } from "lodash";
import { extractEmail } from "extract-email-address";
import { isValidEmail } from "@/lib/utils";
import { useServerQuery } from "@/hooks/use-server-query";
import dynamic from "next/dynamic";

const MailEditor = dynamic(() => import("@/components/editor/mail-editor"), {
  ssr: false
});

export const InviteModal = () => {
  const [isShowInvite, setIsShowInvite] = useState(false);
  const { isOpen, type, onClose, onOpen } = useModal();

  const { domain } = useParams<{ domain: string }>();

  const { data: server, isLoading } = useServerQuery({ serverId: domain });
  const searchParams = useSearchParams();
  const [quill, setQuill] = useState<Quill | null>(null);
  const pathname = usePathname();
  const [formik, setFormik] = useState<FormikProps<any> | null>(null);
  const router = useRouter();
  const { data: me } = useMemberQuery({ memberId: "me" });
  const [subject, setSubject] = useState<string>("You're Invited to Join Our Community!");
  useEffect(() => {
    const invite = searchParams.get("invite");
    if (invite && invite === "true" && domain) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("invite");
      const p = params.toString();
      onOpen("invite");
      router.push(`${pathname}${p ? `?${p}` : ""}`);
    }
  }, [searchParams, pathname, router, onOpen, domain]);

  useEffect(() => {
    if (isLoading) return;
    if (me?.firstName) {
      setSubject(`${me.firstName} has invited you to join ${server?.name} community!`);
    }
  }, [server, isLoading, me]);

  const isModalOpen = useMemo(() => {
    return isOpen && type === "invite" && !!me;
  }, [isOpen, type, me]);

  const handleClose = () => onClose();

  const handleSubmit = (formik: any, search?: string) => {
    let emails: string[] = formik.values.emails || [];
    if (search) {
      const _emails = [...extractEmail(search).map((e) => e.email), ...formik.values.emails];
      const set = new Set(_emails);
      emails = filter(Array.from(set), (value: string) => isValidEmail(value));
    }
    if (formik.isValid && (emails.length > 0 || search || formik.values.csv)) {
      setIsShowInvite(true);
      setFormik(formik);
      if (quill) {
        quill.setContents([
          {
            insert:
              "We’d love to have you as part of our community! Join us to connect with like-minded individuals, share ideas, and grow together.\n\n"
          },
          { insert: "Looking forward to seeing you there!\n\n" },
          { insert: "Thank you!\n" }
        ]);
      }
    } else {
      setIsShowInvite(false);
      formik.submitForm();
    }
  };

  const handleInvite = () => {
    if (formik && quill) {
      const description_html = quill.getSemanticHTML();
      const finaldescription = description_html.replace(
        /<img([^>]*)>/g,
        '<img$1 style="max-width:100%; height:auto;">'
      );
      formik.setFieldValue("body", btoa(encodeURIComponent(finaldescription)));
      formik.setFieldValue("subject", btoa(encodeURIComponent(subject)) || subject);
      formik.submitForm();
      setIsShowInvite(false);
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-full-50px w-[550px] overflow-hidden p-0 bg-none border-none backdrop-blur-none shadow-none"
        style={{
          height: "calc(100dvh - 90px)",
          background: "transparent"
        }}
        isShowCloseBtn={false}
      >
        <div className="w-full max-h-full flex flex-col overflow-hidden">
          <div className="rounded-lg border-2 border-border bg-modal overflow-hidden py-4 shadow-lg backdrop-blur-20 flex flex-col my-auto">
            <div className="flex-shrink-0 px-4">
              <DialogHeader className="flex-shrink-0 pb-l pr-l border-b-1">
                <div className="flex ">
                  {isShowInvite && (
                    <div className="flex items-center" onClick={() => setIsShowInvite(false)}>
                      <ArrowLeft2 className="h-6 w-6 mr-m"></ArrowLeft2>
                    </div>
                  )}
                  <DialogTitle>Invite Member</DialogTitle>
                  <DialogClose className="ms-auto">
                    <X className="h-6 w-6" />
                  </DialogClose>
                </div>
              </DialogHeader>
            </div>
            <div
              style={{ display: !isShowInvite ? "flex" : "none" }}
              className="flex-shrink-1 overflow-hidden"
            >
              <ScrollArea className="h-full px-4">
                <div className="w-full flex-shrink-1 flex flex-col ">
                  <div className="w-full flex flex-row items-center justify-center">
                    <img
                      src={"/static/images/Invite-member.png"}
                      alt="Invite Logo"
                      className="w-[88%]"
                    />
                  </div>
                  <div className="flex flex-col gap-xl w-full pt-4 overflow-hidden">
                    <div className="flex flex-row items-center">
                      <div className="rounded-full h-fit p-[16px] gap-[10px]  bg-[#F5F6F9] mr-xl">
                        <Link1 className="min-w-[24px] min-h-[24px]"></Link1>
                      </div>
                      <ShareLink />
                    </div>
                    <div className="flex flex-row items-center">
                      <div className="rounded-full h-fit p-[16px] gap-[10px]  bg-[#F5F6F9] mr-xl">
                        <Sms className="min-w-[24px] min-h-[24px]"></Sms>
                      </div>
                      <div className="flex flex-col w-full">
                        <Text variant="body_one_strong" className="mb-2 ">
                          Email
                        </Text>

                        <ManualUpload handleSubmit={handleSubmit} />
                      </div>
                    </div>
                    <div className="flex flex-row items-center">
                      <div className="rounded-full h-fit p-[16px] gap-[10px]  bg-[#F5F6F9] mr-xl">
                        <Document className="min-w-[24px] min-h-[24px]"></Document>
                      </div>
                      <BulkUpload handleSubmit={handleSubmit} />
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>
            <div style={{ display: isShowInvite ? "block" : "none" }} className="px-l">
              <Text variant={"body_two"} className="mt-xl mb-s font-semibold">
                Subject
              </Text>

              <Input
                variant="medium-s"
                className="rounded-xs w-full px-l"
                inputClassName="w-full"
                placeholder="Subject"
                value={subject}
                onChange={(e: any) => setSubject(e.target.value)}
              />

              <Text variant={"body_two"} className="mt-xl mb-s font-semibold">
                Email
              </Text>

              <Box className="field-border">
                <div className="">
                  <MailEditor quill={quill} setQuill={setQuill} />
                </div>
              </Box>
              <Button className="w-full mt-xl py-m" onClick={handleInvite}>
                Invite
              </Button>
            </div>
            <div className="flex-shrink-0 px-4"></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
